import { useState } from 'react';
import { useI18n } from '../lib/i18n';
import {
  INVOICE_LANGUAGES, INVOICE_LANGUAGE_LABELS, type InvoiceLanguage,
} from '../lib/invoice-text';
import { Invoice, type InvoiceData } from './Invoice';

/**
 * The receipt with a language picker over it.
 *
 * Separate from the portal's own language: the person printing works in
 * Russian while the customer in front of them may want Georgian, and the
 * choice is made per receipt rather than by switching the whole interface.
 * The buttons are hidden on paper.
 */
export function PrintableInvoice({ data }: { data: InvoiceData }) {
  const { t, language } = useI18n();
  const [printIn, setPrintIn] = useState<InvoiceLanguage>(
    // The staff language is the likeliest answer, so it is the one preselected.
    language === 'en' ? 'en' : 'ru',
  );

  return (
    <>
      {/* Print hides everything outside #invoice, so these never reach paper. */}
      <div className="mb-2 flex flex-wrap items-center justify-end gap-1.5">
        <span className="mr-1 text-[0.68rem] text-ink-faint">{t('invoice.printIn')}</span>
        {INVOICE_LANGUAGES.map((code) => (
          <button
            key={code}
            onClick={() => setPrintIn(code)}
            aria-pressed={printIn === code}
            className={`min-h-9 rounded-lg px-3 text-xs font-medium transition-colors ${
              printIn === code
                ? 'bg-raised text-ink'
                : 'text-ink-faint hover:bg-raised/60 hover:text-ink-dim'
            }`}
          >
            {INVOICE_LANGUAGE_LABELS[code]}
          </button>
        ))}
      </div>

      <Invoice data={data} language={printIn} />
    </>
  );
}
